import { builderDispatch } from '../_utils/ui';
import CartItemsChangeEvents from './cart-items-change-events';

class Analytics {
  // Analytics style options

  constructor(items) {
    this.items = items;
    this.items.prints = $.extend({
      quantity: 0,
      sizes: [],
      delivery_options: [],
    }, this.items.prints);
  }

  invoke() {
    if (typeof s_omni !== 'object') return;

    s_omni.channel = window.location.hostname;
    s_omni.prop1 = 'Cart';
    s_omni.events = this._events();
    s_omni.products = this._products();

    if (this._isEmpty()) {
      s_omni.pageName = 'Shopping Cart Empty';
      s_omni.prop2 = 'Shopping Cart Empty';
      s_omni.prop21 = '';
    } else {
      // Get the delivery options and quantity
      s_omni.pageName = 'Shopping Cart Cart';
      s_omni.prop2 = 'Shopping Cart Cart';
      s_omni.prop8 = 'Walmart Photo: LiveLink';
      s_omni.prop21 = `InCart:${this._fulfilmentMethods()}`; // Fulfilment Methods List
      s_omni.eVar33 = `${this._quantity()}`;
    }

    s_omni.t();
  };

  _quantity() {
    if (this.__quantity) {
      return this.__quantity;
    }

    this.__quantity = this._allItems().reduce((total, item) => total + item.quantity, 0);

    return this.__quantity;
  };

  _isEmpty() {
    return this._quantity() === 0;
  };

  _fulfilmentMethods() {
    const fulfilmentMethods = {};
    // Collect the delivery options
    this._allItems().forEach((item) => {
      const fulfilmentMethod = this.#translateDeliveryOption(item.delivery_options[0]);
      if (fulfilmentMethod) {
        fulfilmentMethods[fulfilmentMethod] = true;
      }
    });

    return Object.keys(fulfilmentMethods).join(',');
  };

  _allItems() {
    return [].concat(this._printsItems(), this._prismItems());
  };

  _products() {
    return [].concat(this._printProducts(), this._prismProducts()).map((item) => item.join(';')).join(',');
  };

  _printProducts() {
    return this._printsProductItems().map((item) => [
      'walmartphotocenter:',
      (item.partner_id || item.sku),
      item.quantity,
      (item.total_price / 100),
      '',
      'evar30=Walmart.com',
    ]);
  };

  _prismProducts() {
    return this._prismProductItems().map((item) => [
      'walmartphotocenter:',
      (item.partner_id || item.sku),
      item.quantity,
      (item.cost / 100),
      '',
      'evar30=Walmart.com',
    ]);
  };

  #translateDeliveryOption(option) {
    option = option || {};
    switch (option.type) {
      case 'PICKUP':
        if (parseInt(option.time_hours) === 1) {
          return '1-HOUR';
        }
        if (option.time_hours <= 24) {
          return 'PUT';
        }
        if (option.time_hours <= 24 * 5) {
          return 'S2S';
        }
        break;
      case 'POST':
        if (option.time_hours <= 24 * 5) {
          return 'S2H';
        }
        break;
    }
  }
}

class AnalyticsLoaded extends Analytics {
  _events() {
    return 'scView';
  };

  _printsItems() {
    return this.items.prints;
  };

  _prismItems() {
    return this.items.prism;
  };

  _printsProductItems() {
    return this.items.prints.sizes;
  };

  _prismProductItems() {
    return this.items.prism;
  };
}

class AnalyticsRemovedPrints extends Analytics {
  constructor(items, size) {
    super(items);

    this.size = size;
  }

  _events() {
    return 'scRemove';
  };

  // prints items "are no longer in the cart"
  _printsItems() {
    return $.extend({
      quantity: 0,
      sizes: [],
      delivery_options: [],
    }, this.items.prints);
  };

  // but prism items are
  _prismItems() {
    return this.items.prism;
  };

  // need to pass the not-yet-deleted prints summary to omniture
  _printsProductItems() {
    if (this.size) {
      return [this.size];
    }
    return this.items.prints.sizes;
  };

  // omniture doesn't want to know
  _prismProductItems() {
    return [];
  };
}

class AnalyticsUnremovedPrints extends AnalyticsRemovedPrints {
  _events() {
    return 'scAdd';
  };
}

class AnalyticsRemovedPrism extends Analytics {
  // `prism_id` is passed in but not used, and seems empty the `products` event variable
  // this differs to the behaviour of the prints equivalent
  constructor(items, _prism_id) {
    super(items);
  }

  _events() {
    return 'scRemove';
  };

  // prints are still here
  _printsItems() {
    return this.items.prints;
  };

  // we're removing just this particular item
  _prismItems() {
    // this array with the item in question removed
    return this.items.prism.filter(function (item) {
      return item.id != this.prism_id;
    }, this);
  };

  // omniture doesn't want to know
  _printsProductItems() {
    return [];
  };

  // omniture is only interested in the removed item
  _prismProductItems() {
    return this.items.prism.filter(function (item) {
      return item.id == this.prism_id;
    }, this);
  };
}

class AnalyticsUnremovedPrism extends AnalyticsRemovedPrism {
  _events() {
    return 'scAdd';
  };
}

class AnalyticsChangedQuantity extends Analytics {
  constructor(items, prism_id, quantity) {
    super(items);

    this.quantity = parseInt(quantity, 10);

    this.item = items.prism.find((item) => item.id == prism_id);
  }

  _events() {
    return this.quantity < this.item.quantity ? 'scRemove' : 'scAdd';
  };

  _printsItems() {
    return this.items.prints;
  };

  _prismItems() {
    return this.items.prism;
  };

  // omniture doesn't want to know
  _printsProductItems() {
    return [];
  };

  // omniture is only interested in the adjusted item
  _prismProductItems() {
    const delta = Math.abs(this.quantity - this.item.quantity);

    const item = $.extend({}, this.item, {
      quantity: delta,
      cost: (this.item.cost / this.item.quantity) * delta,
    });

    return [item];
  };
}

builderDispatch.register((payload) => {
  let analytics;

  switch (payload.type) {
    case CartItemsChangeEvents.LOADED:
      analytics = new AnalyticsLoaded(payload.items);
      break;
    case CartItemsChangeEvents.REMOVED_PRINTS:
      analytics = new AnalyticsRemovedPrints(payload.items, payload.print_size);
      break;
    case CartItemsChangeEvents.UNREMOVED_PRINTS:
      analytics = new AnalyticsUnremovedPrints(payload.items, payload.print_size);
      break;
    case CartItemsChangeEvents.REMOVED_PRISM:
      analytics = new AnalyticsRemovedPrism(payload.items, payload.prism_id);
      break;
    case CartItemsChangeEvents.UNREMOVED_PRISM:
      analytics = new AnalyticsUnremovedPrism(payload.items, payload.prism_id);
      break;
    case CartItemsChangeEvents.QUANTITY_CHANGED:
      analytics = new AnalyticsChangedQuantity(payload.items, payload.prism_id, payload.quantity);
      break;
  }

  try {
    analytics && analytics.invoke();
  } catch (e) {
    window.console && window.console.warn && window.console.warn(e);
  }
});
